import { useEffect } from "react";
import { motion } from "framer-motion";
import { ArrowLeft, MapPin } from "lucide-react";
import { scrollTop } from "@/lib/scroll";

const SECTIONS = [
    {
        title: "Agreement to Terms",
        body: "These Terms of Use govern your use of the Las Vegas Restroom Locator iOS app and this website (lasvegasrestroomlocator.com), operated by the app developer (\"we\", \"us\"). By downloading the app or using this site, you agree to these terms. If you don't agree, please don't use the app or site.",
    },
    {
        title: "The Service",
        body: "The app provides a map of publicly accessible restrooms in Las Vegas, including access notes, filters, and offline availability. Restroom data is community-sourced and maintained on a best-effort basis: properties renovate, change access policies, and close facilities without notice. Always follow on-site signage and staff instructions — they override anything shown in the app.",
    },
    {
        title: "Ad-Free Subscription",
        body: "The optional Ad-Free subscription costs $2.99 per month. Payment is charged to your Apple ID at confirmation of purchase. The subscription renews automatically every month unless you cancel at least 24 hours before the end of the current period. You can manage or cancel the subscription anytime in your App Store account settings. Cancelling stops future charges; you keep ad-free access until the end of the paid period. Refunds are handled by Apple under Apple's own policies. If you delete the app or switch devices, use the Restore Purchases option on the paywall to recover your subscription.",
    },
    {
        title: "Acceptable Use",
        body: "Don't misuse the app or this site: no scraping the map data in bulk, no submitting false restroom reports, no attempting to disrupt the service, and no using the app to trespass in areas a property has marked as private or guests-only.",
    },
    {
        title: "Intellectual Property",
        body: "The app, this website, the map data compilation, and all branding are our property. Casino, hotel, and venue names referenced in the app (Bellagio, Caesars Palace, The Venetian, and others) are trademarks of their respective owners and are used only to identify locations. No endorsement by any venue is implied.",
    },
    {
        title: "Disclaimers",
        body: "The app and site are provided \"as is\" without warranties of any kind. We don't guarantee that any listed restroom will be open, accessible, or available when you arrive, and we're not responsible for decisions made by property owners or staff.",
    },
    {
        title: "Limitation of Liability",
        body: "To the maximum extent permitted by law, we are not liable for any indirect, incidental, or consequential damages arising from your use of the app or this site — including a restroom being closed when you needed it most. Our total liability is limited to the amount you paid us in the 12 months before the claim (which, for most users, is $0).",
    },
    {
        title: "Apple End User License",
        body: "Your use of the iOS app is also governed by Apple's standard Licensed Application End User License Agreement, available in the App Store. Where these terms and Apple's agreement conflict, Apple's agreement controls for the app itself.",
    },
    {
        title: "Governing Law",
        body: "These terms are governed by the laws of the State of Nevada, USA, without regard to conflict-of-law rules. Disputes will be handled in the state or federal courts located in Clark County, Nevada.",
    },
    {
        title: "Changes & Contact",
        body: "If these terms change, the updated version will be posted at this URL with a new \"last updated\" date, and material changes will be noted in the app's release notes. Questions? Email lasvegasbathroomlocator@gmail.com or use the support form on the home page.",
    },
];

export default function Terms() {
    useEffect(() => {
        scrollTop();
    }, []);

    return (
        <div className="min-h-screen bg-[#06070B]" data-testid="terms-page">
            <header className="border-b border-white/5 bg-[#06070B]/80 backdrop-blur-xl">
                <div className="mx-auto flex max-w-4xl items-center justify-between px-5 py-4 sm:px-8">
                    <a href="/" className="flex items-center gap-2.5" data-testid="terms-logo">
                        <span className="flex h-9 w-9 items-center justify-center rounded-lg border border-neon-cyan/40 bg-neon-cyan/10">
                            <MapPin className="h-5 w-5 text-neon-cyan" strokeWidth={2.2} />
                        </span>
                        <span className="font-display text-xs font-bold tracking-[0.18em] text-white sm:text-sm">
                            LV RESTROOM <span className="text-neon-cyan neon-cyan">LOCATOR</span>
                        </span>
                    </a>
                    <a
                        href="/"
                        data-testid="terms-back-link"
                        className="flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-slate-400 transition-colors duration-300 hover:text-neon-cyan"
                    >
                        <ArrowLeft className="h-4 w-4" />
                        Back to site
                    </a>
                </div>
            </header>

            <main className="mx-auto max-w-4xl px-5 py-16 sm:px-8 sm:py-24">
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
                >
                    <p className="mb-4 font-mono text-xs uppercase tracking-[0.3em] text-neon-gold">
                        Legal — Last updated August 2026
                    </p>
                    <h1 className="font-display text-3xl font-extrabold tracking-tight text-white sm:text-4xl lg:text-5xl">
                        TERMS OF <span className="text-neon-magenta neon-magenta">USE</span>
                    </h1>
                    <p className="mt-6 max-w-2xl text-base leading-relaxed text-slate-400">
                        The house rules for using the Las Vegas Restroom Locator app and this
                        website — including how the Ad-Free subscription works.
                    </p>
                </motion.div>

                <div className="mt-14 space-y-5">
                    {SECTIONS.map((s, i) => (
                        <motion.section
                            key={s.title}
                            initial={{ opacity: 0, y: 24 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-60px" }}
                            transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1], delay: Math.min(i * 0.05, 0.3) }}
                            className="glass-panel rounded-2xl p-6 sm:p-7"
                            data-testid={`terms-section-${i + 1}`}
                        >
                            <h2 className="mb-3 flex items-baseline gap-3 text-lg font-semibold text-white">
                                <span className="font-mono text-xs text-neon-magenta">
                                    {String(i + 1).padStart(2, "0")}
                                </span>
                                {s.title}
                            </h2>
                            <p className="text-sm leading-relaxed text-slate-400">{s.body}</p>
                        </motion.section>
                    ))}
                </div>

                <p className="mt-14 border-t border-white/5 pt-8 text-xs text-slate-600" data-testid="terms-footer-note">
                    © 2026 Las Vegas Restroom Locator. These terms apply to the iOS app and to
                    lasvegasrestroomlocator.com.
                </p>
            </main>
        </div>
    );
}
